import { EquipmentExtended, EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { ProductClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { Answer } from '@frontend/question/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { StockClient } from '@frontend/stock/api';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

export interface EquipmentWithProduct extends IEquipment {
    product: IProduct;
}

interface ViewProps {
    product?: IProduct;
    equipment?: Partial<EquipmentExtended>;
    answers: Answer[];

    availableSlot: Slot | null;

    isLoading: boolean;
    isProcessing: boolean;

    onBack: () => void;
    onSubmit: () => void;
}

export const useConfirmReturn = (): ViewProps => {
    const location = useLocation();
    const { store, onBack, onSubmit, navigationState, updateWizardData, wizardData } = useScreen();
    const { product, equipment, budgetUse, returnReason, answers, userOverwrite } = wizardData;
    const stateEquipment = location.state?.equipment as IEquipment | undefined;
    const functionality = store?.functionality;

    const [availableSlot, setAvailableSlot] = useState<Slot | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const transactionContext = useWizardTransaction({
        store: store,
        wizardId: store?.wizard.id
    });
    const packageContext = useWizardPackage({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    const { transactionWorkflowId, dropOffPackageWorkflowId, pickUpPackageWorkflowId } = useWizardSettings(store);
    const workflowInfo =
        transactionWorkflowId && dropOffPackageWorkflowId
            ? {
                  transactionWf: transactionWorkflowId,
                  dropWf: dropOffPackageWorkflowId,
                  pickWf: pickUpPackageWorkflowId
              }
            : null;

    useEffect(() => {
        if (product && equipment) return;
        if (product && !equipment && product.type === ProductType.CONSUMABLE) return;
        if (equipment) {
            if (!product) {
                ProductClient.fetchProduct(equipment.account_id!, equipment.product_id!).then((product) => {
                    updateWizardData({
                        product
                    });
                });
            }
            return;
        }
        if (stateEquipment) {
            ProductClient.fetchProduct(stateEquipment.account_id, stateEquipment.product_id).then((product) => {
                updateWizardData({
                    product,
                    equipment: stateEquipment
                });
            });
        }
        return;
    }, [equipment, product]);

    useEffect(() => {
        if (!product) return;
        if (product.type === ProductType.EQUIPMENT && !equipment) return;

        const accountId = navigationState.iot?.account_id;
        const spotId = navigationState.iot?.spot_id;

        if (equipment && equipment.state === EquipmentStateName.EQUIPMENT_AVAILABLE) {
            getSlotForAvailableEquipment(equipment as IEquipment)
                .then((slot) => {
                    if (slot) setAvailableSlot(slot);
                    else if (accountId && spotId) {
                        SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                            account_id: accountId,
                            product_id: product.id,
                            equipment_id: equipment.id,
                            transaction_type: TransactionType.LENDING,
                            application: functionality?.application,
                            feature: functionality?.feature,
                            functionality_id: functionality?.id
                        })
                            .then((result) => {
                                if (result.results.length > 0) {
                                    setAvailableSlot(result.results[0]);
                                }
                            })
                            .finally(() => {
                                setIsLoading(false);
                            });
                    }
                })
                .finally(() => setIsLoading(false));
            return;
        }
        if (accountId && spotId) {
            SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                account_id: accountId,
                product_id: product.id,
                equipment_id: equipment?.id,
                transaction_type: equipment
                    ? TransactionType.LENDING
                    : product.type === ProductType.CONSUMABLE
                      ? TransactionType.VENDING
                      : TransactionType.LENDING,
                application: functionality?.application,
                feature: functionality?.feature,
                functionality_id: functionality?.id
            })
                .then((result) => {
                    if (result.results.length > 0) {
                        setAvailableSlot(result.results[0]);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [product, equipment, navigationState.iot, functionality?.application, functionality?.feature, functionality?.id]);

    const handleSubmit = async () => {
        if (!functionality || !availableSlot || !workflowInfo || !product || !store?.wizard) return;
        setIsProcessing(true);
        try {
            const transaction = await TransactionClient.postTransaction(store.wizard.account_id, {
                type: product.type === ProductType.EQUIPMENT ? TransactionType.LENDING : TransactionType.VENDING,
                workflow_id: workflowInfo.transactionWf,
                spot_id: navigationState.iot!.spot_id,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                user_id: userOverwrite?.id ?? navigationState.user?.id,
                iot_id: navigationState.iot!.id,
                application: functionality.application,
                feature: functionality.feature,
                state: TransactionStateName.TRANSACTION_CREATED,
                return_reason: returnReason
            });

            if (navigationState.user?.id) {
                await TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.DROPPED_OFF_BY,
                    user_id: userOverwrite?.id || navigationState.user.id
                });
            }
            if (userOverwrite && navigationState.user?.id) {
                await TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.INITIATED_BY,
                    user_id: navigationState.user.id
                });
            }
            await transactionContext.localUpdateTransaction({
                id: transaction.id,
                state: transaction.state,
                workflow_id: transaction.workflow_id,
                type: transaction.type,
                user_id: transaction.user_id,
                spot_id: transaction.spot_id,
                module_id: transaction.module_id,
                slot_id: transaction.slot_id,
                iot_id: transaction.iot_id,
                application: transaction.application,
                feature: transaction.feature
            });

            const dropPkg = await PackageClient.postPackage(transaction.account_id, transaction.id, {
                state: PackageStateName.PACKAGE_CREATED,
                workflow_id: workflowInfo.dropWf,
                user_id: transaction.user_id,
                quantity: 1,
                type: PackageType.DROP_OFF,
                spot_id: availableSlot.spot_id,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                product_id: product.id,
                equipment_id: equipment?.id,
                budget_id: budgetUse?.budget_id,
                application: transaction.application,
                feature: transaction.feature
            });
            await packageContext.localUpdatePackage(dropPkg);
            if (workflowInfo.pickWf) {
                await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    state: PackageStateName.PACKAGE_CREATED,
                    workflow_id: workflowInfo.pickWf,
                    user_id: transaction.user_id,
                    quantity: 1,
                    type: PackageType.AUDIT,
                    spot_id: availableSlot.spot_id,
                    module_id: availableSlot.module_id,
                    slot_id: availableSlot.id,
                    product_id: product.id,
                    equipment_id: equipment?.id,
                    budget_id: budgetUse?.budget_id,
                    dependent_package_id: dropPkg.id,
                    application: transaction.application,
                    feature: transaction.feature
                });
            }
            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        product,
        equipment,
        answers: answers || [],
        availableSlot,
        isLoading,
        isProcessing,
        onBack,
        onSubmit: handleSubmit
    };
};

const getSlotForAvailableEquipment = async (equipment: IEquipment) => {
    const stock = await StockClient.fetchStocks({ equipment_id: equipment.id, account_id: equipment.account_id });
    if (stock.results.length === 0) {
        Logger.warn('Return of available equipment that is not in stock', { equipment: equipment });
        return null;
    }
    const found = stock.results[0];
    return await SlotClient.fetchSlot(equipment.account_id, found.spot_id, found.module_id, found.slot_id);
};
